<template>
  <div class="ml-12 mr-12 my-2 clients-container">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <!-- Confirmation Dialog -->
    <ConfirmationDialog
      v-model:visible="showConfirmationDialog"
      :title="'Delete Client'"
      :message="`Are you sure you want to delete ${itemToDelete?.firstName} ${itemToDelete?.surname}? This action cannot be undone.`"
      @confirm="confirmDelete"
      @cancel="showConfirmationDialog = false"
    />

    <!-- Header Section -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="pa-4" style="border-radius: 15px; background: linear-gradient(135deg, #002060 0%, #001547 100%);">
          <h2 class="text-white mb-2">{{ pageTitle }}</h2>
          <p class="text-white text-opacity-80 mb-0">
            Manage {{ clientType === 'individual' ? 'individual client' : clientType === 'business' ? 'business client' : 'trust client' }} information, membership levels, and status
          </p>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Row -->
    <v-row class="mb-4">
      <!-- Search input -->
      <v-col cols="12" sm="6" md="2">
        <v-text-field
          v-model="search"
          :label="`Search ${clientType === 'individual' ? 'Clients' : clientType === 'business' ? 'Companies' : 'Trusts'}`"
          clearable
          class="elevation-0"
          @input="debouncedSearch"
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          append-inner-icon="mdi-magnify"
          variant="solo"
        />
      </v-col>

      <!-- Membership filter -->
      <v-col cols="12" sm="6" md="2">
        <v-select
          v-model="membershipFilter"
          :items="membershipOptions"
          label="Filter by Membership"
          clearable
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          variant="solo"
          @update:model-value="debouncedSearch"
        />
      </v-col>



      <!-- Date filter -->
      <v-col cols="12" sm="6" md="2">
        <v-text-field
          v-model="dateFilter"
          label="Filter by Date"
          clearable
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          variant="solo"
          @input="debouncedSearch"
          hint="Format: MM/DD/YYYY"
        />
      </v-col>

      <!-- Add Client button -->
      <v-col cols="12" sm="6" md="4" class="d-flex align-center justify-start">
        <v-btn
          color="#002060"
          class="text-white"
          @click="navigateToAddClient"
          prepend-icon="mdi-plus"
          rounded
          flat
        >
          {{ getAddButtonText() }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Data table -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="filteredClients"
          :items-per-page="10"
          :loading="loading"
          style="border-radius: 17px;"
          loading-text="Loading... Please wait"
          :no-data-text="`No ${clientType === 'individual' ? 'clients' : clientType === 'business' ? 'companies' : 'trusts'} found. Add your first ${clientType === 'individual' ? 'client' : clientType === 'business' ? 'company' : 'trust'} to get started!`"
          class="custom-header elevation-3"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :footer-props="{ 'items-per-page-options': [5,10,15,20] }"
        >
            <template v-slot:item.membership="{ item }">
              <v-chip
                :color="getMembershipColor(item.membership)"
                text-color="white"
                small
              >
                {{ item.membership || 'basic' }}
              </v-chip>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip
                :color="getStatusColor(item.status)"
                text-color="white"
                small
              >
                {{ item.status || 'active' }}
              </v-chip>
            </template>

            <template v-slot:item.membershipStatus="{ item }">
              <v-chip
                :color="getMembershipStatusColor(item.membershipStatus)"
                text-color="white"
                small
              >
                {{ item.membershipStatus || 'Active' }}
              </v-chip>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn 
                variant="text" 
                @click.stop="viewClient(item)"
                color="#002060"
                size="small"
                class="mr-1"
              >
                View
              </v-btn>
  
              <v-btn 
                variant="text" 
                @click.stop="showDeleteConfirmation(item)"
                color="error"
                size="small"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
  
      
    </div>
  </template>
  
  <script>
  import { debounce } from 'lodash';
  import { db } from '../firebaseConfig';
  import { collection, onSnapshot, query, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
  import { ActivityLogger } from '../utils/activityLogger';
  import { mapGetters } from 'vuex';
  import NotificationDialog from '../components/NotificationDialog.vue';
import ConfirmationDialog from '../components/ConfirmationDialog.vue';
  
  export default {
      components: {
    NotificationDialog,
    ConfirmationDialog
  },
    computed: {
      ...mapGetters(['user', 'userRole']),
      
      canEditClient() {
        // Employee cannot edit client billing or membership levels
              // Only Admin and Executive can edit clients
      return ['Admin', 'Executive'].includes(this.userRole);
      }
    },
    data() {
      return {
              showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
      showConfirmationDialog: false,
      itemToDelete: null,
        search: '',
        dateFilter: null,
        loading: false,
        clientType: 'individual', // Default to individual clients

        // core data
        clients: [],

        // table headers - will be computed based on client type
        headers: [],
        membershipFilter: null,
        membershipOptions: [
          { title: 'All Memberships', value: null },
          { title: 'Domestic', value: 'Domestic' },
          { title: 'Individual', value: 'Individual' },
          { title: 'Individual & Domestic', value: 'Individual & Domestic' }
        ],


        sortBy: ['clientId'],
        sortDesc: [false],
      };
    },

    computed: {
      pageTitle() {
        switch (this.clientType) {
          case 'individual':
            return 'Individual Clients';
          case 'business':
            return 'Companies';
          case 'trust':
            return 'Trusts';
          default:
            return 'Clients';
        }
      },

      headers() {
        switch (this.clientType) {
          case 'individual':
            return [
     
              { title: 'CIF Number',     key: 'cifNumber',          sortable: true,  align: 'center' },
              { title: 'Name',           key: 'firstName',          sortable: true,  align: 'center' },
              { title: 'Surname',        key: 'surname',            sortable: true,  align: 'center' },
              { title: 'ID Number',      key: 'idNumber',           sortable: true,  align: 'center' },
              { title: 'Email',          key: 'email',              sortable: true,  align: 'center' },
         
              { title: 'Mem. Number',    key: 'membershipNumber',   sortable: true,  align: 'center' },
              { title: 'Date Added',     key: 'dateAdded',          sortable: true,  align: 'center' },
              { title: 'Actions',        key: 'actions',            sortable: false, align: 'center' },
            ];
          case 'business':
            return [
              { title: 'CIF Number',     key: 'cifNumber',          sortable: true,  align: 'center' },
              { title: 'Company Name',   key: 'companyName',        sortable: true,  align: 'center' },
              { title: 'Reg. Number',    key: 'registrationNumber', sortable: true,  align: 'center' },
              { title: 'VAT Number',     key: 'vatNumber',          sortable: true,  align: 'center' },
              { title: 'Business Type',  key: 'businessType',       sortable: true,  align: 'center' },
              { title: 'Email',          key: 'email',              sortable: true,  align: 'center' },
              { title: 'Membership',     key: 'membership',         sortable: true,  align: 'center' },
              { title: 'Status',         key: 'status',             sortable: true,  align: 'center' },
              { title: 'Date Added',     key: 'dateAdded',          sortable: true,  align: 'center' },
              { title: 'Actions',        key: 'actions',            sortable: false, align: 'center' },
            ];
          case 'trust':
            return [
              { title: 'CIF Number',     key: 'cifNumber',          sortable: true,  align: 'center' },
              { title: 'Trust Name',     key: 'trustName',          sortable: true,  align: 'center' },
              { title: 'Trust Number',   key: 'trustNumber',        sortable: true,  align: 'center' },
              { title: 'Trust Type',     key: 'trustType',          sortable: true,  align: 'center' },
              { title: 'Reg. Date',      key: 'registrationDate',   sortable: true,  align: 'center' },
              { title: 'Email',          key: 'email',              sortable: true,  align: 'center' },
              { title: 'Membership',     key: 'membership',         sortable: true,  align: 'center' },
              { title: 'Status',         key: 'status',             sortable: true,  align: 'center' },
              { title: 'Date Added',     key: 'dateAdded',          sortable: true,  align: 'center' },
              { title: 'Actions',        key: 'actions',            sortable: false, align: 'center' },
            ];
          default:
            return [
              { title: 'CIF Number',     key: 'cifNumber',          sortable: true,  align: 'center' },
              { title: 'Name',           key: 'firstName',          sortable: true,  align: 'center' },
              { title: 'Surname',        key: 'surname',            sortable: true,  align: 'center' },
              { title: 'Email',          key: 'email',              sortable: true,  align: 'center' },
              { title: 'Membership',     key: 'membership',         sortable: true,  align: 'center' },
              { title: 'Status',         key: 'status',             sortable: true,  align: 'center' },
              { title: 'Date Added',     key: 'dateAdded',          sortable: true,  align: 'center' },
              { title: 'Actions',        key: 'actions',            sortable: false, align: 'center' },
            ];
        }
      },
      
      filteredClients() {
        const filtered = this.clients.filter(client => {
          // Filter by client type first
          const clientType = client.clientType || 'individual';
          const matchesType = clientType === this.clientType;
          
          // Build search fields based on client type
          let searchFields = [client.cifNumber, client.email, client.membership, client.status, client.membershipStatus, client.membershipNumber];
          
          switch (clientType) {
            case 'individual':
              searchFields.push(client.firstName, client.surname);
              break;
            case 'business':
              searchFields.push(client.companyName, client.registrationNumber, client.vatNumber, client.businessType);
              break;
            case 'trust':
              searchFields.push(client.trustName, client.trustNumber, client.trustType, client.registrationDate);
              break;
          }
          
          const matchesSearch = this.search
            ? searchFields.filter(Boolean).some(f => f.toString().toLowerCase().includes(this.search.toLowerCase()))
            : true;
          const matchesDate = this.dateFilter
            ? client.dateAdded === this.dateFilter
            : true;
          const matchesMembership = this.membershipFilter
            ? client.membership === this.membershipFilter
            : true;
          return matchesType && matchesSearch && matchesDate && matchesMembership;
        });
        
        return filtered;
      }
    },

    methods: {
      getClients() {
        this.loading = true;
        // Listen for real-time updates from Firestore
        if (this.unsubscribeClients) this.unsubscribeClients();
        const clientsQuery = query(collection(db, 'clients'), orderBy('dateAdded', 'desc'));
        this.unsubscribeClients = onSnapshot(clientsQuery, (snapshot) => {
          this.clients = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          this.loading = false;
        }, (error) => {
          console.error('Firestore error:', error);
          this.clients = [];
          this.loading = false;
        });
      },
      getMembershipColor(membership) {
        const colors = {
          'Domestic': 'green',
          'Individual': 'blue',
          'Individual & Domestic': 'purple'
        };
        return colors[membership] || 'blue';
      },
      getStatusColor(status) {
        const colors = {
          active: 'success',
          inactive: 'error',
          suspended: 'warning'
        };
        return colors[status] || 'success';
      },
      getMembershipStatusColor(membershipStatus) {
        const colors = {
          'Active': 'success',
          'Inactive': 'error',
          'Pending': 'warning',
          'Suspended': 'orange',
          'Expired': 'grey'
        };
        return colors[membershipStatus] || 'success';
      },
      viewClient(item) {
        console.log('Viewing client:', item);
        this.$router.push(`/client-details/${item.id}`);
      },

      editClient(item) {
        console.log('Editing client:', item);
        // Navigate to edit page based on client type
        const clientType = item.clientType || 'individual';
        let routeName = 'AddClient';
        
        switch (clientType) {
          case 'business':
            routeName = 'AddBusiness';
            break;
          case 'trust':
            routeName = 'AddTrust';
            break;
          default:
            routeName = 'AddClient';
        }
        
        // Navigate to the add form with edit parameters
        this.$router.push({ 
          name: routeName, 
          query: { 
            edit: 'true', 
            clientId: item.id 
          } 
        });
      },

      async toggleClientStatus(item) {
        try {
          const newStatus = item.status === 'active' ? 'inactive' : 'active';
          await updateDoc(doc(db, 'clients', item.id), {
            status: newStatus,
            updatedAt: new Date().toISOString()
          });
          console.log(`Client ${item.id} status updated to ${newStatus}`);
          
          // Log client status change activity
          const changedBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
          await ActivityLogger.statusChanged(changedBy, this.user?.uid, `${item.firstName} ${item.surname}`, newStatus);
          
          // Show success notification
          this.notificationType = 'success';
          this.notificationTitle = 'Success!';
          this.notificationMessage = `Client status updated to ${newStatus} successfully!`;
          this.showNotification = true;
        } catch (error) {
          console.error('Error updating client status:', error);
          
          // Show error notification
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Failed to update client status. Please try again.';
          this.showNotification = true;
        }
      },
      showDeleteConfirmation(item) {
        this.itemToDelete = item;
        this.showConfirmationDialog = true;
      },

      async confirmDelete() {
        if (!this.itemToDelete) return;
        
        try {
          await deleteDoc(doc(db, 'clients', this.itemToDelete.id));
          console.log(`Client ${this.itemToDelete.id} deleted successfully`);
          
          // Log client deletion activity
          const deletedBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
          await ActivityLogger.clientDeleted(deletedBy, this.user?.uid, `${this.itemToDelete.firstName} ${this.itemToDelete.surname}`);
          
          // Show success notification
          this.notificationType = 'success';
          this.notificationTitle = 'Success!';
          this.notificationMessage = `Client ${this.itemToDelete.firstName} ${this.itemToDelete.surname} deleted successfully!`;
          this.showNotification = true;
        } catch (error) {
          console.error('Error deleting client:', error);
          
          // Show error notification
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Failed to delete client. Please try again.';
          this.showNotification = true;
        }
      },

      cancelDelete() {
        this.itemToDelete = null;
        this.showConfirmationDialog = false;
      },

      getAddButtonText() {
        switch (this.clientType) {
          case 'individual':
            return 'Add Client';
          case 'business':
            return 'Add Business';
          case 'trust':
            return 'Add Trust';
          default:
            return 'Add Client';
        }
      },

      navigateToAddClient() {
        // Navigate to different routes based on client type
        switch (this.clientType) {
          case 'individual':
            this.$router.push({ name: 'AddClient' });
            break;
          case 'business':
            this.$router.push({ name: 'AddBusiness' });
            break;
          case 'trust':
            this.$router.push({ name: 'AddTrust' });
            break;
          default:
            this.$router.push({ name: 'AddClient' });
        }
      },

      logClientSearch() {
        if (this.search && this.search.trim()) {
          const currentUser = this.$store.getters.user;
          const currentUserId = currentUser?.uid;
          const currentUserName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Unknown';
          
          ActivityLogger.clientSearched(currentUserName, currentUserId, this.search, 'Clients Page');
        }
      },

      debouncedSearch: debounce(function () {
        this.getClients();
        this.logClientSearch();
      }, 300),
    },

    watch: {
      dateFilter() {
        this.debouncedSearch();
      },
      membershipFilter() {
        this.debouncedSearch();
      },

      '$route.query.type'(newType) {
        this.clientType = newType || 'individual';
        this.getClients();
      }
    },

    created() {
      // Get client type from query parameter
      this.clientType = this.$route.query.type || 'individual';
      this.getClients();
    },
    beforeUnmount() {
      if (this.unsubscribeClients) this.unsubscribeClients();
    }
  };
  </script>
  
  <style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');
  
  * {
    font-family: 'Montserrat', sans-serif;
  }
  
  .custom-header {
  background-color: #e8e8e8;
}

.status-toggle-btn {
  transition: all 0.2s ease;
}

.status-toggle-btn:hover {
  transform: scale(1.1);
}

/* Custom tooltip styling */
:deep(.v-tooltip .v-overlay__content) {
  background-color: #002060 !important;
  color: white !important;
  font-size: 0.85rem !important;
  padding: 8px 12px !important;
  border-radius: 6px !important;
  box-shadow: 0 4px 12px rgba(0, 32, 96, 0.3) !important;
}
  </style>
  